import React, { useEffect } from "react";
import Image from "next/image";
import NextLink from "next/link";
import { Box, Heading, Link } from "@chakra-ui/react";

import { PostParsed } from "src/services/contentful/post/postParser";
import {
  Wrapper,
  CoverImageWrapper,
  Body,
  Title,
  Excerpt,
  PostDate,
  ReadMoreButton,
} from "./styles";
import LinkComponent from "src/components/atoms/Link";
import { Asset, AssetFile } from "contentful";

const postDateTemplate = {
  year: "numeric",
  month: "short",
  day: "numeric",
} as const;

const parseUrl = (imageUrl: string): string => {
  let url = `${imageUrl}`;
  if (typeof window !== "undefined" && url && url.startsWith("//images")) {
    url = imageUrl
      .replace("//images", `${window.location.protocol}//images`)
      .replace("url=", `url=${window.location.protocol}%3A`);
  }

  return url;
};

export type PostCardProps = {
  post: PostParsed;
  variant?: "variant1" | "variant2";
};
const PostCard: React.FC<PostCardProps> = ({
  post,
  variant = "variant1",
  ...rest
}) => {
  const [pst, setPst] = React.useState<PostParsed>(post);

  useEffect(() => {
    setPst(post);
  }, [post]);

  const coverImage = pst?.coverImage as AssetFile;
  return (
    <Wrapper $variant={variant} {...rest}>
      {pst?.coverImage && pst?.coverImage?.url ? (
        <CoverImageWrapper data-testid="CoverImageWrapper">
          <Image
            src={`${parseUrl(coverImage?.url)}`}
            alt={coverImage?.fileName}
            layout="fill"
            objectFit="cover"
          />
        </CoverImageWrapper>
      ) : null}
      <Body>
        <Heading as="h4" size="lg">
          <Link
            href={`/blog/${pst?.slug}`}
            as={NextLink}
            passHref
            color="brandPrimary.500"
          >
            {pst?.title}
          </Link>
        </Heading>
        <Box color="brandAccent.600">
          <PostDate dateTime={pst?.date}>
            {new Date(pst?.date).toLocaleDateString("en-US", postDateTemplate)}
          </PostDate>
        </Box>
        <Excerpt>{pst?.excerpt}</Excerpt>
        <Link
          as={NextLink}
          href={`/blog/${pst?.slug}`}
          aria-label={`Read more on ${pst?.title}`}
          variant="brand-solid-accent"
        >
          Read more
        </Link>
      </Body>
    </Wrapper>
  );
};

export default React.memo(PostCard);
